/**
 * Specialty profile — the single source of truth for how a clinic's
 * `business_type` shapes the clinical UI. Before this, the same "which
 * specialties care about vitals / labs / have a chart" lists were hard-coded and
 * duplicated across the encounter screen, the patient page, and SpecialtyCharts.
 * Centralising it here means tuning a specialty's experience is a one-file edit,
 * and every surface (visit, patient page, quick-create, terminology) stays in
 * sync.
 *
 * Specialties are grouped into FAMILIES so we don't maintain 16 bespoke configs;
 * per-business_type overrides layer on top where a specialty differs.
 */
export type SpecialtyFamily = "aesthetic" | "dental" | "medical" | "procedural";

export interface SpecialtyProfile {
	/** Coarse grouping used for shared defaults (vitals/labs/terminology). */
	family: SpecialtyFamily;
	/** True when the clinic has a dedicated specialty chart (skin map,
	 *  odontogram, antenatal, growth, …). False for general / other. */
	hasChart: boolean;
	/** Vitals are core to this specialty's encounter (render expanded) vs
	 *  incidental (collapse behind a one-tap "add"). */
	vitalsRelevant: boolean;
	/** Lab ordering is core to this specialty's encounter. */
	labsRelevant: boolean;
	/** What an encounter is called: a medical "visit" or an aesthetic/therapy
	 *  "session". Drives terminology (e.g. «جلسة» vs «زيارة»). */
	encounterNoun: "visit" | "session";
	/** Default tab when opening a patient — specialties whose chart IS the
	 *  workspace (derma skin map, dental odontogram) open straight to it. */
	patientDefaultTab: "summary" | "clinical";
}

const AESTHETIC = new Set<string>([
	"cosmetic",
	"dermatology",
	"hair_transplant",
]);
const DENTAL = new Set<string>(["dental", "orthodontics"]);
const MEDICAL = new Set<string>([
	"general",
	"internal_medicine",
	"cardiology",
	"pediatric",
	"gynecology",
]);
/** No dedicated specialty chart — a plain clinical record is enough. */
const NO_CHART = new Set<string>(["general", "other"]);
/** Treatment is delivered in "sessions", not "visits". */
const SESSION_NOUN = new Set<string>([
	"cosmetic",
	"dermatology",
	"hair_transplant",
	"physiotherapy",
]);
/** Open the clinical chart first — it's the doctor's main workspace here. */
const CHART_FIRST = new Set<string>([
	"cosmetic",
	"dermatology",
	"hair_transplant",
	"dental",
	"orthodontics",
]);

export function specialtyFamily(
	bt: string | null | undefined,
): SpecialtyFamily {
	if (!bt) return "medical";
	if (AESTHETIC.has(bt)) return "aesthetic";
	if (DENTAL.has(bt)) return "dental";
	if (MEDICAL.has(bt)) return "medical";
	// ophthalmology, physiotherapy, orthopedics, ent, psychiatry, other
	return "procedural";
}

export function specialtyProfile(
	bt: string | null | undefined,
): SpecialtyProfile {
	const family = specialtyFamily(bt);
	const medical = family === "medical";
	return {
		family,
		hasChart: bt != null && !NO_CHART.has(bt),
		vitalsRelevant: medical,
		labsRelevant: medical,
		encounterNoun: bt != null && SESSION_NOUN.has(bt) ? "session" : "visit",
		patientDefaultTab:
			bt != null && CHART_FIRST.has(bt) ? "clinical" : "summary",
	};
}

/** Convenience predicates (thin wrappers over specialtyProfile). */
export function isVitalsRelevant(bt: string | null | undefined): boolean {
	return specialtyProfile(bt).vitalsRelevant;
}
export function isLabsRelevant(bt: string | null | undefined): boolean {
	return specialtyProfile(bt).labsRelevant;
}
export function specialtyHasChart(bt: string | null | undefined): boolean {
	return specialtyProfile(bt).hasChart;
}
